import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { X, User, Envelope, MagnifyingGlass, Bell, Warning, Upload, FileXls, FileCsv, Trash } from '@phosphor-icons/react'
import toast from 'react-hot-toast'
import ExcelJS from 'exceljs'
import Papa from 'papaparse'

export default function InviteMemberModal({ isOpen, onClose, onSuccess }) {
  const [mode, setMode] = useState('single') // 'single' or 'bulk'
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(false)
  const [userStatus, setUserStatus] = useState(null) // null, 'unassigned', 'assigned', 'not_exists'
  const [inviteSent, setInviteSent] = useState(false)
  const [existingUserData, setExistingUserData] = useState(null)
  const [orgId, setOrgId] = useState(null)
  
  // Single invite form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  })

  // Bulk invite
  const [uploadedFile, setUploadedFile] = useState(null)
  const [extractedEmails, setExtractedEmails] = useState([])
  const [bulkResults, setBulkResults] = useState([])
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadOrgId()
      // Reset form
      setMode('single')
      setFormData({
        name: '',
        email: '',
        phone: ''
      })
      setUserStatus(null)
      setExistingUserData(null)
      setInviteSent(false)
      setUploadedFile(null)
      setExtractedEmails([])
      setBulkResults([])
    }
  }, [isOpen])

  async function loadOrgId() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      setOrgId(userData?.org_id)
    } catch (err) {
      console.error('Load org error:', err)
    }
  }

  function handleClose() {
    setMode('single')
    setFormData({ name: '', email: '', phone: '' })
    setUserStatus(null)
    setExistingUserData(null)
    setInviteSent(false)
    setUploadedFile(null)
    setExtractedEmails([])
    setBulkResults([])
    onClose()
  }

  // ============= SINGLE INVITE LOGIC =============
  
  async function checkUserStatus() {
    if (!formData.email.trim()) {
      toast.error('Please enter an email address')
      return
    }

    setChecking(true)
    setUserStatus(null)
    setExistingUserData(null)

    try {
      // Check if user exists in the organization
      const { data: users } = await supabase
        .from('users')
        .select('id, name, email, phone, role, leadership_level')
        .eq('email', formData.email.toLowerCase().trim())
        .eq('org_id', orgId)
        .maybeSingle()

      if (users) {
        // Check if user is a leader (team, group, or subgroup leader)
        if (users.leadership_level && users.leadership_level !== 'member') {
          // User is a leader - get their leadership assignment
          const { data: leaderMemberships } = await supabase
            .from('group_members')
            .select(`
              group_id,
              groups!inner (
                id,
                name,
                depth
              )
            `)
            .eq('user_id', users.id)
          
          const leadershipGroup = leaderMemberships?.[0]?.groups
          const leadershipTitle = users.leadership_level === 'team' ? 'Team Leader' : 
                                  users.leadership_level === 'group' ? 'Group Leader' : 
                                  'Subgroup Leader'
          
          setUserStatus('assigned')
          setExistingUserData({
            ...users,
            assignmentInfo: leadershipGroup ? 
              `${leadershipTitle} of ${leadershipGroup.name}` 
              : leadershipTitle
          })
        } else {
          // User is a regular member - check all their assignments
          const { data: memberships } = await supabase
            .from('group_members')
            .select(`
              group_id,
              groups!inner (
                id,
                name,
                depth
              )
            `)
            .eq('user_id', users.id)

          if (memberships && memberships.length > 0) {
            // Get all their groups at each level
            const teams = memberships.filter(m => m.groups.depth === 1)
            const groups = memberships.filter(m => m.groups.depth === 2)
            const subgroups = memberships.filter(m => m.groups.depth === 3)

            let assignmentInfo = []
            if (teams.length > 0) assignmentInfo.push(`Teams: ${teams.map(t => t.groups.name).join(', ')}`)
            if (groups.length > 0) assignmentInfo.push(`Groups: ${groups.map(g => g.groups.name).join(', ')}`)
            if (subgroups.length > 0) assignmentInfo.push(`Subgroups: ${subgroups.map(s => s.groups.name).join(', ')}`)

            setUserStatus('assigned')
            setExistingUserData({
              ...users,
              assignmentInfo: assignmentInfo.join(' | ')
            })
          } else {
            setUserStatus('unassigned')
            setExistingUserData(users)
            setFormData(prev => ({
              ...prev,
              name: users.name || prev.name,
              phone: users.phone || prev.phone
            }))
          }
        }
      } else {
        setUserStatus('not_exists')
      }

    } catch (error) {
      console.error('Error checking user:', error)
      toast.error('Failed to check user status')
    } finally {
      setChecking(false)
    }
  }

  async function sendGeneralInvitation() {
    if (!existingUserData) return

    setLoading(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()

      // Create general invitation record (no specific group assignment)
      const { error: inviteError } = await supabase
        .from('invitations')
        .insert({
          inviter_id: user.id,
          invitee_email: existingUserData.email,
          invitee_id: existingUserData.id,
          status: 'pending',
          invitation_type: 'general' // Mark as general org invitation
        })

      if (inviteError) {
        if (inviteError.code === '23505') {
          toast.error('This person already has a pending invitation.')
          handleClose()
          return
        }
        throw inviteError
      }

      setInviteSent(true)
      
      if (onSuccess) onSuccess()
    } catch (error) {
      console.error('Invitation error:', error)
      toast.error('Failed to send invitation')
    } finally {
      setLoading(false)
    }
  }

  async function createNewUser() {
    // Validate form
    if (!formData.email || !formData.name) {
      toast.error('Email and Name are required')
      return
    }

    setLoading(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      // Get organization name
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', orgId)
        .single()
      
      if (orgError) {
        console.error('Failed to fetch org name:', orgError)
        toast.error('Failed to fetch organization details')
        setLoading(false)
        return
      }
      
      const orgName = orgData?.name || 'Organization'
      
      // Generate invite token
      const token = crypto.randomUUID()
      
      // Create invite link record
      const { error: inviteError } = await supabase
        .from('invite_links')
        .insert({
          org_id: orgId,
          token: token,
          recipient_email: formData.email,
          created_by: user.id,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
          used: false
        })
      
      if (inviteError) {
        console.error('Failed to create invite link:', inviteError)
        toast.error('Failed to create invitation')
        setLoading(false)
        return
      }
      
      // Call Edge Function to send signup invitation with token
      console.log('📧 Calling send-invite-email Edge Function...')
      
      try {
        const response = await Promise.race([
          supabase.functions.invoke('send-invite-email', {
            body: {
              recipientEmail: formData.email,
              recipientName: formData.name || 'Member',
              signupLink: `${window.location.origin}/signup?token=${token}`,
              orgName: orgName,
              memberType: 'new'
            }
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Email send timeout after 30s')), 30000)
          )
        ])

        console.log('📧 Edge Function response:', response)
        
        if (response.error) {
          console.error('📧 Edge Function error:', response.error)
          throw response.error
        }
      } catch (edgeFunctionError) {
        console.error('📧 Edge Function failed:', edgeFunctionError)
        // Don't throw - invitation link is created, just email failed
        toast.error('Invitation link created, but email failed to send. You can share the link manually.')
      }

      setInviteSent(true)
      toast.success('Invitation link created successfully!')
      
      if (onSuccess) onSuccess()
    } catch (error) {
      console.error('Create user error:', error)
      toast.error('Failed to send invitation')
    } finally {
      setLoading(false)
    }
  }

  // ============= BULK INVITE LOGIC =============

  function handleFileUpload(e) {
    const file = e.target.files[0]
    if (!file) return

    setUploadedFile(file)
    
    const fileExtension = file.name.split('.').pop().toLowerCase()

    if (fileExtension === 'csv') {
      parseCSV(file)
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      parseExcel(file)
    } else {
      toast.error('Please upload a CSV or Excel file')
      setUploadedFile(null)
    }
  }

  function parseCSV(file) {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        extractEmailsFromData(results.data)
      },
      error: (error) => {
        console.error('CSV parse error:', error)
        toast.error('Failed to parse CSV file')
        setUploadedFile(null)
      }
    })
  }

  async function parseExcel(file) {
    try {
      const arrayBuffer = await file.arrayBuffer()
      const workbook = new ExcelJS.Workbook()
      await workbook.xlsx.load(arrayBuffer)
      
      const firstSheet = workbook.worksheets[0]
      const jsonData = []
      
      // Get headers from first row
      const headerRow = firstSheet.getRow(1)
      const headers = []
      headerRow.eachCell((cell, colNumber) => {
        headers[colNumber] = cell.value
      })
      
      // Convert rows to JSON objects
      firstSheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return // Skip header row
        
        const rowData = {}
        row.eachCell((cell, colNumber) => {
          const header = headers[colNumber]
          if (header) {
            rowData[header] = cell.value
          }
        })
        
        if (Object.keys(rowData).length > 0) {
          jsonData.push(rowData)
        }
      })
      
      extractEmailsFromData(jsonData)
    } catch (error) {
      console.error('Excel parse error:', error)
      toast.error('Failed to parse Excel file')
      setUploadedFile(null)
    }
  }

  function extractEmailsFromData(data) {
    // Look for column named 'email' (case insensitive)
    const emails = []
    
    data.forEach((row, index) => {
      // Find email column (case insensitive)
      const emailKey = Object.keys(row).find(key => 
        key.toLowerCase() === 'email'
      )
      
      if (emailKey && row[emailKey]) {
        let email = row[emailKey]
        
        // Handle Excel hyperlinks (ExcelJS returns objects for hyperlinked cells)
        if (typeof email === 'object' && email.text) {
          email = email.text
        }
        
        email = String(email).trim()
        
        if (email && validateEmail(email)) {
          emails.push(email.toLowerCase())
        }
      }
    })

    // Remove duplicates
    const uniqueEmails = [...new Set(emails)]
    
    if (uniqueEmails.length === 0) {
      toast.error('No valid email addresses found in the "email" column')
      setUploadedFile(null)
      return
    }

    setExtractedEmails(uniqueEmails)
    toast.success(`Found ${uniqueEmails.length} valid email address${uniqueEmails.length > 1 ? 'es' : ''}`)
  }

  function validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  async function processBulkInvites() {
    if (extractedEmails.length === 0) {
      toast.error('No emails to process')
      return
    }

    setProcessing(true)
    const results = []

    try {
      const { data: { user } } = await supabase.auth.getUser()

      // Get organization name once for all bulk invites
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('name')
        .eq('id', orgId)
        .single()
      
      if (orgError) {
        console.error('Failed to fetch org name:', orgError)
        toast.error('Failed to fetch organization details')
        setProcessing(false)
        return
      }
      
      const orgName = orgData?.name || 'Organization'

      for (const email of extractedEmails) {
        try {
          // Check if user exists
          const { data: existingUser } = await supabase
            .from('users')
            .select('id, name, email, leadership_level')
            .eq('email', email)
            .eq('org_id', orgId)
            .maybeSingle()

          if (existingUser) {
            // Check assignments
            const { data: memberships } = await supabase
              .from('group_members')
              .select(`
                group_id,
                groups!inner (
                  id,
                  name,
                  depth
                )
              `)
              .eq('user_id', existingUser.id)

            let status = 'unassigned'
            let assignment = 'No assignments'
            
            if (existingUser.leadership_level && existingUser.leadership_level !== 'member') {
              status = 'assigned'
              const leadershipGroup = memberships?.[0]?.groups
              assignment = leadershipGroup ? `${existingUser.leadership_level} of ${leadershipGroup.name}` : existingUser.leadership_level
            } else if (memberships && memberships.length > 0) {
              status = 'assigned'
              const groupNames = memberships.map(m => m.groups.name).join(', ')
              assignment = groupNames
            }

            // Try to send invitation if unassigned
            if (status === 'unassigned') {
              const { error: inviteError } = await supabase
                .from('invitations')
                .insert({
                  inviter_id: user.id,
                  invitee_email: email,
                  invitee_id: existingUser.id,
                  status: 'pending',
                  invitation_type: 'general'
                })

              if (inviteError && inviteError.code === '23505') {
                results.push({
                  email,
                  status: 'pending_invite',
                  message: 'Already has pending invitation'
                })
              } else if (inviteError) {
                results.push({
                  email,
                  status: 'error',
                  message: 'Failed to send invite'
                })
              } else {
                results.push({
                  email,
                  status: 'invited',
                  message: 'Invitation sent'
                })
              }
            } else {
              results.push({
                email,
                status: 'already_assigned',
                message: `Already assigned: ${assignment}`
              })
            }
          } else {
            // User doesn't exist - send signup invitation
            // Generate invite token
            const token = crypto.randomUUID()
            
            // Create invite link record
            const { error: inviteError } = await supabase
              .from('invite_links')
              .insert({
                org_id: orgId,
                token: token,
                recipient_email: email,
                created_by: user.id,
                expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                used: false
              })
            
            if (inviteError) {
              results.push({
                email,
                status: 'error',
                message: 'Failed to create invite link'
              })
              continue
            }
            
            const response = await supabase.functions.invoke('send-invite-email', {
              body: {
                recipientEmail: email,
                recipientName: 'Member',
                signupLink: `${window.location.origin}/signup?token=${token}`,
                orgName: orgName,
                memberType: 'new'
              }
            })

            if (response.error) {
              results.push({
                email,
                status: 'error',
                message: 'Failed to send signup invite'
              })
            } else {
              results.push({
                email,
                status: 'signup_sent',
                message: 'Signup invitation sent'
              })
            }
          }
        } catch (error) {
          console.error(`Error processing ${email}:`, error)
          results.push({
            email,
            status: 'error',
            message: error.message || 'Processing failed'
          })
        }

        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      setBulkResults(results)
      
      const successCount = results.filter(r => 
        r.status === 'invited' || r.status === 'signup_sent'
      ).length
      
      toast.success(`Processed ${results.length} emails. ${successCount} invitations sent.`)
      
      if (onSuccess) onSuccess()
    } catch (error) {
      console.error('Bulk invite error:', error)
      toast.error('Failed to process bulk invites')
    } finally {
      setProcessing(false)
    }
  }

  function removeFile() {
    setUploadedFile(null)
    setExtractedEmails([])
    setBulkResults([])
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900">Invite Member</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} weight="light" />
          </button>
        </div>

        <div className="p-6">
          {/* Mode Selection */}
          {!userStatus && bulkResults.length === 0 && (
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setMode('single')}
                className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${
                  mode === 'single'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Single Invite
              </button>
              <button
                onClick={() => setMode('bulk')}
                className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${
                  mode === 'bulk'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Bulk Upload
              </button>
            </div>
          )}

          {/* ===== SINGLE INVITE MODE ===== */}
          {mode === 'single' && (
            <>
              {/* Push Notification Sent Success - For Unassigned Users */}
              {userStatus === 'unassigned' && inviteSent && (
                <>
                  <div className="mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                      <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Bell size={32} weight="fill" className="text-green-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Notification Sent!</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        A push notification has been sent to <strong>{existingUserData?.name}</strong> ({existingUserData?.email}) inviting them to join your organization.
                      </p>
                      <div className="bg-white border border-green-200 rounded-xl p-3">
                        <p className="text-xs text-gray-700">
                          They can accept or decline the invitation from their app.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleClose}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Done
                  </button>
                </>
              )}

              {/* Email Sent Success - For New Users */}
              {userStatus === 'not_exists' && inviteSent && (
                <>
                  <div className="mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                      <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Envelope size={32} weight="fill" className="text-green-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Invitation Email Sent!</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        An email has been sent to <strong>{formData.email}</strong> with instructions to join your organization.
                      </p>
                      <div className="bg-white border border-green-200 rounded-xl p-3">
                        <p className="text-xs text-gray-700">
                          When they sign up using the link, they'll be added to your organization.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleClose}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Done
                  </button>
                </>
              )}

              {/* Already Assigned Warning */}
              {userStatus === 'assigned' && (
                <>
                  <div className="mb-6">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-yellow-100 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                          <Warning size={24} weight="fill" className="text-yellow-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-2">Member Already Assigned</h3>
                          <p className="text-sm text-gray-700 mb-3">
                            <strong>{existingUserData?.name}</strong> ({existingUserData?.email}) is already assigned in your organization.
                          </p>
                          <div className="bg-white border border-yellow-200 rounded-xl p-3">
                            <p className="text-sm font-medium text-gray-900 mb-1">Current Assignment:</p>
                            <p className="text-sm text-gray-700">
                              {existingUserData?.assignmentInfo}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleClose}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Done
                  </button>
                </>
              )}

              {/* Initial Form - Before User Check */}
              {!userStatus && (
                <>
                  <div className="space-y-4 mb-6">
                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Envelope
                          size={18}
                          weight="light"
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="member@example.com"
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        We'll check if this person is in your organization
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Smart Invite:</strong> We'll automatically detect if they're in the system and handle it accordingly.
                    </p>
                  </div>

                  <button
                    onClick={checkUserStatus}
                    disabled={checking || !formData.email}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {checking ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Checking...
                      </>
                    ) : (
                      <>
                        <MagnifyingGlass size={20} weight="light" />
                        Search Member
                      </>
                    )}
                  </button>
                </>
              )}

              {/* Unassigned User - Show Additional Fields */}
              {userStatus === 'unassigned' && !inviteSent && (
                <>
                  <div className="mb-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>{existingUserData?.name}</strong> is in your organization but not assigned to any team. Send them an invitation!
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={sendGeneralInvitation}
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Bell size={20} weight="light" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </>
              )}

              {/* New User - Show Complete Form */}
              {userStatus === 'not_exists' && !inviteSent && (
                <>
                  <div className="mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                      <p className="text-sm text-green-800">
                        This email is not in your organization. Fill in their details to send a signup invitation.
                      </p>
                    </div>

                    <div className="space-y-4">
                      {/* Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <div className="relative">
                          <User
                            size={18}
                            weight="light"
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          />
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="John Doe"
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>

                      {/* Phone (Optional) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number (Optional)
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+1234567890"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={createNewUser}
                    disabled={loading || !formData.name}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending Invitation...
                      </>
                    ) : (
                      <>
                        <Envelope size={20} weight="light" />
                        Send Signup Invitation
                      </>
                    )}
                  </button>
                </>
              )}
            </>
          )}

          {/* ===== BULK UPLOAD MODE ===== */}
          {mode === 'bulk' && (
            <>
              {bulkResults.length === 0 ? (
                <>
                  {/* File Upload */}
                  {!uploadedFile && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Upload CSV or Excel File
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="bulk-file-upload"
                        />
                        <label
                          htmlFor="bulk-file-upload"
                          className="cursor-pointer flex flex-col items-center gap-3"
                        >
                          <Upload size={48} weight="light" className="text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">
                              CSV or Excel files with an "email" column
                            </p>
                          </div>
                        </label>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                        <p className="text-sm text-blue-800 font-medium mb-2">File Requirements:</p>
                        <ul className="text-xs text-blue-700 space-y-1">
                          <li>• Must contain a column named "email" (case insensitive)</li>
                          <li>• One email address per row</li>
                          <li>• Duplicate emails will be removed automatically</li>
                          <li>• Invalid email formats will be skipped</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* File Preview */}
                  {uploadedFile && extractedEmails.length > 0 && (
                    <>
                      <div className="mb-6">
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                              {uploadedFile.name.endsWith('.csv') ? (
                                <FileCsv size={32} weight="fill" className="text-green-600 flex-shrink-0" />
                              ) : (
                                <FileXls size={32} weight="fill" className="text-green-600 flex-shrink-0" />
                              )}
                              <div>
                                <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                                <p className="text-sm text-gray-600 mt-1">
                                  {extractedEmails.length} valid email address{extractedEmails.length > 1 ? 'es' : ''} found
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={removeFile}
                              className="text-red-600 hover:text-red-700 transition-colors"
                            >
                              <Trash size={20} weight="light" />
                            </button>
                          </div>
                        </div>

                        {/* Email List Preview */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4 max-h-60 overflow-y-auto">
                          <p className="text-sm font-medium text-gray-700 mb-2">Email Addresses:</p>
                          <div className="space-y-1">
                            {extractedEmails.map((email, index) => (
                              <div key={index} className="text-sm text-gray-600 py-1 border-b border-gray-100 last:border-0">
                                {index + 1}. {email}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                        <p className="text-sm text-yellow-800">
                          <strong>Note:</strong> Each email will be checked against your organization. Invitations will only be sent to valid, unassigned members.
                        </p>
                      </div>

                      <button
                        onClick={processBulkInvites}
                        disabled={processing}
                        className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        {processing ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <Bell size={20} weight="light" />
                            Send Bulk Invitations
                          </>
                        )}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* Bulk Results */}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Bulk Invite Results</h3>
                    
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {bulkResults.filter(r => r.status === 'invited' || r.status === 'signup_sent').length}
                        </p>
                        <p className="text-xs text-green-700">Invited</p>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-yellow-600">
                          {bulkResults.filter(r => r.status === 'already_assigned' || r.status === 'pending_invite').length}
                        </p>
                        <p className="text-xs text-yellow-700">Skipped</p>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-red-600">
                          {bulkResults.filter(r => r.status === 'error').length}
                        </p>
                        <p className="text-xs text-red-700">Errors</p>
                      </div>
                    </div>

                    {/* Detailed Results */}
                    <div className="bg-white border border-gray-200 rounded-xl max-h-96 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Email</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkResults.map((result, index) => (
                            <tr key={index} className="border-t border-gray-100">
                              <td className="py-3 px-4 text-gray-900">{result.email}</td>
                              <td className="py-3 px-4">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  result.status === 'invited' || result.status === 'signup_sent' 
                                    ? 'bg-green-100 text-green-700'
                                    : result.status === 'already_assigned' || result.status === 'pending_invite'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {result.status.replace(/_/g, ' ')}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-gray-600 text-xs">{result.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <button
                    onClick={handleClose}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Done
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}